import { useId, useMemo, useState } from 'react';

import Badge from 'react-bootstrap/Badge';
import FormControl from 'react-bootstrap/FormControl';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Popover from 'react-bootstrap/Popover';

import { Button } from '../buttons';
import { SimpleCheckboxInput } from '../forms';

/** Значение мультифильтра колонки (совпадает с опциями колонок в формах). */
export interface HeaderFilterOption {
  value: string;
  label: string;
}

export interface HeaderFilterProps {
  /** Заголовок колонки — показывается в шапке поповера. */
  title?: string;
  /** Все уникальные значения колонки. */
  options: HeaderFilterOption[];
  /** Выбранные значения; пусто — фильтр не активен. */
  value?: HeaderFilterOption[];
  /** Колбэк изменения набора выбранных значений. */
  onChange?(selected: HeaderFilterOption[]): void;
}

// Ширина поповера: не уже 280px (полные названия, например офисов, не должны
// обрезаться) и не шире 480px. Произвольную min/max-ширину утилитами Bootstrap
// не задать, поэтому через style-API компонента — тот же приём, что у
// cellComponent виртуальной таблицы в DataTable.
const POPOVER_WIDTH_STYLE = { minWidth: '280px', maxWidth: '480px' };

// При коротких списках поиск только мешает
const SEARCH_THRESHOLD = 8;

/**
 * Мультифильтр колонки в заголовке DataTable (флаг showTableMultiFilterRow +
 * поля options/optionsValue/optionsFilter у колонки): иконка-воронка рядом с
 * заголовком, по клику — поповер с чекбоксами уникальных значений и поиском.
 * Значения применяются сразу, поповер остаётся открытым — можно отметить
 * несколько значений подряд.
 */
const HeaderFilter = ({ title, options, value, onChange }: HeaderFilterProps) => {
  const [search, setSearch] = useState('');

  const popoverId = useId();

  const selectedValues = useMemo(
    () => new Set((value ?? []).map(option => option.value)),
    [value]
  );

  const visibleOptions = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();

    if (!normalizedSearch) {
      return options;
    }

    return options.filter(option => (
      String(option.label).toLowerCase().includes(normalizedSearch)
    ));
  }, [options, search]);

  const toggleOption = (option: HeaderFilterOption, isSelected: boolean) => {
    const currentValue = value ?? [];
    const nextValue = isSelected
      ? [...currentValue, option]
      : currentValue.filter(selected => selected.value !== option.value);

    onChange?.(nextValue);
  };

  // «Выбрать все» отмечает видимые значения: в связке с поиском это
  // «выбрать всё найденное» (как в автофильтре Excel)
  const selectVisible = () => onChange?.(visibleOptions);
  const clearSelection = () => onChange?.([]);

  const popover = (
    <Popover id={ popoverId } style={ POPOVER_WIDTH_STYLE }>
      { title && (
        <Popover.Title>{ title }</Popover.Title>
      ) }
      <Popover.Content>
        { options.length > SEARCH_THRESHOLD && (
          <FormControl
            size='sm'
            placeholder='Поиск…'
            value={ search }
            onChange={ event => setSearch(event.target.value) }
          />
        ) }
        <div className='pre-scrollable mt-2'>
          {
            visibleOptions.length === 0
              ? <span className='text-muted'>Ничего не найдено</span>
              : visibleOptions.map(option => (
                <SimpleCheckboxInput
                  key={ option.value }
                  label={ option.label }
                  checked={ selectedValues.has(option.value) }
                  rootClassName='py-1'
                  noMarginBottom
                  onChange={ event => toggleOption(option, event.target.checked) }
                />
              ))
          }
        </div>
        <div className='d-flex justify-content-between mt-2'>
          <Button
            variant='link'
            size='sm'
            className='p-0'
            text='Выбрать все'
            onClick={ selectVisible }
          />
          <Button
            variant='link'
            size='sm'
            className='p-0 text-muted'
            text='Очистить'
            onClick={ clearSelection }
          />
        </div>
      </Popover.Content>
    </Popover>
  );

  const selectedCount = selectedValues.size;

  return (
    <OverlayTrigger
      trigger='click'
      rootClose
      placement='bottom-start'
      // поповер рендерим в body — overflow-контейнеры грида иначе его обрежут
      container={ document.body }
      overlay={ popover }
    >
      <Button
        variant='link'
        size='sm'
        className={ selectedCount > 0 ? 'p-0 ml-1 text-primary' : 'p-0 ml-1 text-muted' }
        icon='filter'
      >
        { selectedCount > 0 && (
          <Badge pill variant='primary' className='ml-1'>{ selectedCount }</Badge>
        ) }
        <span className='sr-only'>{ `Фильтр колонки ${ title ? `«${ title }»` : '' }` }</span>
      </Button>
    </OverlayTrigger>
  );
};

export default HeaderFilter;
